/**
 * Garde-fou de publication (spec client 2026-06-11) — LISTE STRICTE.
 *
 * Le bouton "Publier" et le passage du statut de publication à "Publié" ne
 * sont autorisés que si ces critères sont remplis. C'est un prédicat DISTINCT
 * des champs obligatoires (rouges, pipeline ELS) et des champs mailing
 * (oranges, PDF mailing) — ne pas fusionner : chaque liste pilote son circuit.
 *
 * Décisions user :
 *  - Liste client stricte : PAS d'adresse (une annonce peut être publiée avec
 *    la commune seule), pas de typeBien/collaborateur/renon.
 *  - Règle financière par transaction : location → loyer + charges ; vente →
 *    prix. Transaction absente → traitée comme location (cas le plus courant).
 *  - « Charges incluses » RETIRÉ le 2026-09-15 (décision Thibaud, cas FE_A7.5 :
 *    « l'obligation des charges est surtout pour le mailing, pas pour Immoweb
 *    ni le site ») — reste un champ MAILING (orange, `ACTIVE_MAILING_FIELDS`),
 *    ne bloque plus le bouton Publier. Miroir SQL : migration 136.
 *  - "1 photo uploadée" = au moins un fichier dans la galerie (photos[]),
 *    PAS le jugement qualité photosOk.
 *  - Sous option (audit process 2026-07-10) : bloque toujours la publication,
 *    quel que soit le reste — cf. src/lib/sousOption.ts pour le cycle complet.
 *
 * Miroir SQL : migration 082 (trigger sur transition vers 'Publié' + check
 * dans mark_bien_as_published) + migration 087 (ajout sousOption) + migration
 * 136 (retrait charges incluses). Toute évolution ici doit être répliquée là.
 */

export interface PublishGateInput {
  referenceInterne?: string | null;
  projet?: string | null;
  ville?: string | null;
  seraDispoPour?: string | null;
  etage?: string | null;
  nbChambres?: string | null;
  nbSdb?: string | null;
  meuble?: boolean | null;
  consoKwh?: string | null;
  classePeb?: string | null;
  transaction?: string | null;
  loyerMensuel?: string | null;
  chargesMensuelles?: string | null;
  prix?: string | null;
  textesOk?: boolean | null;
  photos?: unknown[] | null;
  sousOption?: boolean | null;
}

export interface PublishGateResult {
  ok: boolean;
  /** Clés des critères manquants (ordre = ordre de la liste client). */
  missing: string[];
}

const filled = (v: string | null | undefined): boolean =>
  typeof v === "string" && v.trim().length > 0;

export function isPublishable(b: PublishGateInput): PublishGateResult {
  const missing: string[] = [];

  if (!filled(b.referenceInterne)) missing.push("referenceInterne");
  if (!filled(b.projet)) missing.push("projet");
  if (!filled(b.ville)) missing.push("ville");
  if (!filled(b.seraDispoPour)) missing.push("seraDispoPour");
  if (!filled(b.etage)) missing.push("etage");
  if (!filled(b.nbChambres)) missing.push("nbChambres");
  if (!filled(b.nbSdb)) missing.push("nbSdb");
  // meuble : tri-état — null = pas saisi ; true/false = réponse valide.
  if (b.meuble !== true && b.meuble !== false) missing.push("meuble");

  const tx = b.transaction || "location";
  const needsRent = tx === "location";
  const needsPrice = tx === "vente";
  if (needsRent && !filled(b.loyerMensuel)) missing.push("loyerMensuel");
  if (needsRent && !filled(b.chargesMensuelles)) missing.push("chargesMensuelles");
  if (needsPrice && !filled(b.prix)) missing.push("prix");

  if (!filled(b.consoKwh)) missing.push("consoKwh");
  if (!filled(b.classePeb)) missing.push("classePeb");
  if (b.textesOk !== true) missing.push("textesOk");
  if (!Array.isArray(b.photos) || b.photos.length === 0) missing.push("photos");

  // Sous option (migration 084) : bien en négociation, dépublié volontairement
  // — pas un "champ manquant" mais un blocage explicite tant que la case est
  // cochée (cf. src/lib/sousOption.ts, décochage géré par sa propre modale).
  if (b.sousOption === true) missing.push("sousOption");

  return { ok: missing.length === 0, missing };
}

/** Clés i18n des libellés humains des critères (pour lister les manquants). */
export const PUBLISH_GATE_LABEL_KEYS: Record<string, string> = {
  referenceInterne: "biens.internalRef",
  projet: "biens.projet",
  ville: "common.city",
  seraDispoPour: "biens.availableDate",
  etage: "biens.etage",
  nbChambres: "biens.nbChambres",
  nbSdb: "biens.nbSdb",
  meuble: "biens.meuble",
  loyerMensuel: "biens.loyerMensuel",
  chargesMensuelles: "biens.chargesMensuelles",
  prix: "biens.prix",
  consoKwh: "biens.consumption",
  classePeb: "biens.pebClass",
  textesOk: "biens.textesOk",
  photos: "publishGate.photos",
  sousOption: "biens.sousOption.badge",
};
